/**
 * Task Cards Block Renderer
 *
 * Receipt for tasks just created via `task_create`: one card per task with a
 * checklist icon, the task key, the title, and the people on it. Rebuilt from
 * the persisted tool-call arguments, so it renders the same live and from
 * history.
 *
 * A card opens the task's details in the canvas where there is a canvas to open
 * — the global chat. Everywhere else, and for anyone who cannot read the Tasks
 * API, it stays the display-only receipt it has always been.
 */

import { ListChecks } from "lucide-react";
import { cn } from "@strivelabs/ui-core/lib/utils";
import { useSlotCanvasStore } from "@/store/slotCanvasStore";
import { useUserRole } from "@/hooks/useUserRole";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { useTrack } from "@/lib/analytics/useTrack";
import { PersonAvatar } from "@/components/common/PersonAvatar";
import { useSlotCanvasContext } from "../slot-canvas/SlotCanvasContext";
import { TaskDetailSlot, taskSlotId } from "./slots/task-detail-slot";
import type { TaskCardTask, UIBlock } from "@/types/ui-blocks";

export interface TaskCardsBlockRendererProps {
  block: UIBlock;
}

/**
 * Owners to show, in stack order. The reviewer is dropped when absent or the
 * same person as the assignee, so a self-assigned task reads as one owner.
 */
function ownersOf(task: TaskCardTask): { role: string; name: string }[] {
  const owners: { role: string; name: string }[] = [];
  if (task.assignee_name) owners.push({ role: "Assignee", name: task.assignee_name });
  if (task.reviewer_name && task.reviewer_name !== task.assignee_name) {
    owners.push({ role: "Reviewer", name: task.reviewer_name });
  }
  return owners;
}

/**
 * The task's id. Cards created before the id was carried have only the key,
 * which the server derives as `TASK-<id>` — so it is read back rather than the
 * row losing its link to the task it names.
 */
function taskIdOf(task: TaskCardTask): string | null {
  return task.id ?? /^TASK-(\d+)$/.exec(task.key)?.[1] ?? null;
}

/**
 * The card's contents, identical whether or not it can be opened.
 *
 * Type here is a step lighter than the board's — 500 against 600 — because a
 * card sitting inside a message is a mention of a task, not a row of a table.
 */
function TaskCardContents({ task }: { task: TaskCardTask }) {
  return (
    <>
      <ListChecks className="size-[18px] shrink-0 text-[#9A9A9A]" aria-hidden="true" />
      <span className="shrink-0 text-[12.5px] font-medium tracking-[-0.01em] text-[#9A9A9A]">
        {task.key}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium tracking-[-0.01em] text-[#181818]">
        {task.title}
      </span>
      <span className="flex shrink-0 items-center">
        {ownersOf(task).map(owner => (
          <PersonAvatar
            key={`${owner.role}-${owner.name}`}
            person={{ display_name: owner.name, image_url: null }}
            title={`${owner.role}: ${owner.name}`}
            className="-ml-[7px] size-[19px] text-[9px] shadow-[0_0_0_2px_#fff] first:ml-0"
          />
        ))}
      </span>
    </>
  );
}

const CARD_CLASS =
  "flex items-center gap-[11px] rounded-[10px] border bg-white px-3.5 py-[11px] text-left transition-[border-color,box-shadow]";

function TaskCard({ task, interactive }: { task: TaskCardTask; interactive: boolean }) {
  const taskId = taskIdOf(task);
  const openSlot = useSlotCanvasStore(state => state.openSlot);
  const trackEvent = useTrack();
  const isOpen = useSlotCanvasStore(
    state => taskId !== null && state.openSlotId === taskSlotId(taskId),
  );

  if (!interactive || !taskId) {
    return (
      <div className={cn(CARD_CLASS, "border-[rgba(0,0,0,0.08)]")}>
        <TaskCardContents task={task} />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        trackEvent(ANALYTICS_EVENTS.TASK_OPENED, { via: "chat_card" });
        openSlot(taskSlotId(taskId));
      }}
      aria-label={`Open ${task.key}: ${task.title}`}
      aria-current={isOpen ? "true" : undefined}
      className={cn(
        CARD_CLASS,
        isOpen
          ? "border-[#9DB9F0] bg-[#FBFAF7] shadow-[0_0_0_1px_#9DB9F0]"
          : "border-[rgba(0,0,0,0.08)] hover:border-[rgba(0,0,0,0.14)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]",
      )}
    >
      <TaskCardContents task={task} />
    </button>
  );
}

export function TaskCardsBlockRenderer({ block }: TaskCardsBlockRendererProps) {
  const { mode } = useSlotCanvasContext();
  const { isGuest, isLoaded } = useUserRole();

  if (block.type !== "task_cards") return null;

  const tasks = block.content.tasks ?? [];
  if (tasks.length === 0) return null;

  // Guests are refused by the Tasks API, so a card they cannot open must not
  // look like one they can — including while their role is still loading,
  // when `isGuest` is false because nothing is known yet.
  const interactive = mode === "canvas" && isLoaded && !isGuest;

  // One slot per task, not per card: two cards naming the same task share a
  // slot id, and a second copy would portal a second panel into the canvas and
  // take the shared registration down with it when its card unmounted.
  const slotTasks = interactive
    ? [...new Map(
        tasks.flatMap(task => {
          const id = taskIdOf(task);
          return id ? [[id, task] as const] : [];
        }),
      ).entries()]
    : [];

  return (
    <>
      <div className="mt-3.5 flex flex-col gap-1">
        {tasks.map((task, index) => (
          <TaskCard key={`${task.key}-${index}`} task={task} interactive={interactive} />
        ))}
      </div>
      {slotTasks.map(([taskId, task]) => (
        <TaskDetailSlot key={taskId} taskId={taskId} title={task.title} />
      ))}
    </>
  );
}
